import { match } from "ts-pattern"
import { useSuspenseQueryGet } from "../../typed-client/hooks/use-suspense-query-get"
import { PrincipalId } from "../../@client/Absents"
import { SimpleUser } from "../../@client/Theasoft_ApiServer_Users"
import { SimplePerson } from "../../@client/Theasoft_ApiServer_Persons"
import { useQueryGet } from "../../typed-client/hooks/use-query-get"
import { Text } from "@mantine/core"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"

type ContractId = { contractId: number }
type PersonId = { personId: number }
type Id = ContractId | PersonId
type SuspenseProps = {
  suspense: true
  children?: (v: SimplePerson) => React.ReactNode
}

type NonSuspenseProps = {
  suspense: false
  children?: (v?: SimplePerson, e?: Error | null) => React.ReactNode
}
type Props = (SuspenseProps | NonSuspenseProps) & Id

export function RenderSimplePerson(props: Props) {
  if ("personId" in props) {
    return props.suspense ? (
      <RenderSimplePersonSuspense
        personId={props.personId}
        children={props.children}
      />
    ) : (
      <RenderSimplePersonNonSuspense
        personId={props.personId}
        children={props.children}
      />
    )
  }
  if ("contractId" in props) {
    return props.suspense ? (
      <RenderSimplePersonForContractSuspense
        contractId={props.contractId}
        children={props.children}
      />
    ) : (
      <RenderSimplePersonForContractNonSuspense
        contractId={props.contractId}
        children={props.children}
      />
    )
  }
  return null
}

function RenderSimplePersonSuspense({
  personId,
  children,
}: {
  personId: number
  children?: (v: SimplePerson) => React.ReactNode
}) {
  const { data } = useSuspenseQueryGet("/api/Person/GetSimplePerson", {
    personId,
  })
  return children ? children(data) : data.name
}

function RenderSimplePersonNonSuspense({
  personId,
  children,
}: {
  personId: number
  children?: (v?: SimplePerson, e?: Error | null) => React.ReactNode
}) {
  const { data, error } = useQueryGet("/api/Person/GetSimplePerson", {
    personId,
  })
  return children ? (
    children(data, error)
  ) : error ? (
    <Text c="red">Error</Text>
  ) : (
    data?.name || null
  )
}

function RenderSimplePersonForContractSuspense({
  contractId,
  children,
}: {
  contractId: number
  children?: (v: SimplePerson) => React.ReactNode
}) {
  const { data } = useSuspenseQueryGet(
    "/api/Person/GetSimplePersonForContract",
    {
      contractId,
    }
  )
  return children ? children(data) : data.name
}

function RenderSimplePersonForContractNonSuspense({
  contractId,
  children,
}: {
  contractId: number
  children?: (v?: SimplePerson, e?: Error | null) => React.ReactNode
}) {
  const { data, error } = useQueryGet(
    "/api/Person/GetSimplePersonForContract",
    {
      contractId,
    }
  )
  return children ? (
    children(data, error)
  ) : error ? (
    <Text c="red">Error</Text>
  ) : (
    data?.name || null
  )
}

export function UserName({ id }: { id: number }) {
  const { data } = useBatchedSimpleUser(id)
  return data?.userName
}

export function UserPersonNameText({ id }: { id: number }) {
  const { data } = useBatchedSimpleUser(id)
  return data?.userName ?? id
}

export function RenderUser({
  id,
  render,
}: {
  id: number
  render: (item: SimpleUser) => React.ReactNode
}) {
  const { data } = useBatchedSimpleUser(id)
  return data ? render(data) : null
}

export function PrincipalName({ id }: { id: PrincipalId }) {
  return match(id)
    .with({ Case: "System" }, () => "System")
    .with({ Case: "UserId" }, (data) => <UserName id={data.Fields} />)
    .exhaustive()
}
